import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart3,
  Calendar,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Users,
  MapPin,
  FileDown,
  Activity,
  Target,
  TrendingUp,
  AlertTriangle,
  Package,
  CalendarDays,
  Filter,
  X,
  Eye,
  Stethoscope
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import { AREAS, REP_DISPLAY_NAMES, getRepDisplayName, REP_DIRECTORY } from "@/config/constants";
import { API } from "@/config/api";
import LocationSelector from "@/components/LocationSelector";

const formatLocation = (area, subregion, suburb) => {
  const parts = [suburb, subregion, area].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "-";
};

const CHART_COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];

const DailyInsights = ({ user, isManager }) => {
  const [noShows, setNoShows] = useState([]);
  const [noFollowUp, setNoFollowUp] = useState([]);
  const [repActivity, setRepActivity] = useState([]);
  const [overdueAppts, setOverdueAppts] = useState([]);
  const [loading, setLoading] = useState(true);

  const todayStr = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      const safeGet = (url) => axios.get(url, { withCredentials: true }).then(r => r.data).catch(() => null);

      // Fetch all data in parallel
      const [appts, visits, overdue] = await Promise.all([
        safeGet(`${API}/upcoming?days=1`),
        safeGet(`${API}/visits?start_date=${todayStr}&end_date=${todayStr}`),
        safeGet(`${API}/crm/reports/overdue`),
      ]);

      // Section A: No-shows today
      try {
        const allAppts = await safeGet(`${API}/calendar/appointments?date_from=${todayStr}&date_to=${todayStr}`);
        const shows = (Array.isArray(allAppts) ? allAppts : allAppts?.appointments || []).filter(a => a.status === "No Show" && a.appointment_date === todayStr);
        setNoShows(isManager ? shows : shows.filter(a => a.rep_name === user?.name || a.rep_name === user?.assigned_rep_name));
      } catch { setNoShows([]); }

      // Section B: Visits with no follow-up
      try {
        const visitList = Array.isArray(visits) ? visits : visits?.visits || [];
        const noFu = visitList.filter(v => !v.next_appointment || v.next_appointment === "" || v.no_followup_scheduled);
        setNoFollowUp(isManager ? noFu : noFu.filter(v => v.rep_name === user?.name || v.rep_name === user?.assigned_rep_name));
      } catch { setNoFollowUp([]); }

      // Section C: Rep Activity Summary
      try {
        const visitList = Array.isArray(visits) ? visits : visits?.visits || [];
        const reps = isManager ? REP_DIRECTORY : [user?.assigned_rep_name || user?.name].filter(Boolean);
        const activity = reps.map(rep => {
          const repVisits = visitList.filter(v => v.rep_name === rep);
          return { rep, visits: repVisits.length, entries: repVisits.length };
        });
        setRepActivity(activity);
      } catch { setRepActivity([]); }

      // Section D: Overdue
      try {
        const cases = overdue?.overdue_cases || [];
        setOverdueAppts(isManager ? cases : cases.filter(c => c.assigned_rep_name === user?.name || c.assigned_rep_name === user?.assigned_rep_name));
      } catch { setOverdueAppts([]); }

      setLoading(false);
    };
    if (user) fetchAll();
  }, [user, isManager, todayStr]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section A: No-Shows Today */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <XCircle className="w-4 h-4 text-red-500" />
            No-Shows Today
            <Badge variant={noShows.length > 0 ? "destructive" : "secondary"} className="ml-auto text-[10px]">{noShows.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {noShows.length > 0 ? (
            <div className="space-y-2">
              {noShows.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-red-50 rounded-lg border border-red-100 text-xs">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="w-3.5 h-3.5 text-red-500" />
                    <span className="font-medium">{a.doctor_name}</span>
                    <span className="text-muted-foreground">{a.practice_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{getRepDisplayName(a.rep_name)}</Badge>
                    <span className="text-muted-foreground">{a.appointment_time || "Unsched."}</span>
                    <Badge variant="destructive" className="text-[10px]">No Show</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 font-medium">No no-shows today</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section B: Visits With No Follow-Up */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Visits With No Follow-Up Scheduled
            <Badge variant="secondary" className="ml-auto text-[10px]">{noFollowUp.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {noFollowUp.length > 0 ? (
            <div className="space-y-2">
              {noFollowUp.map((v, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg border border-amber-100 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{v.doctor_name}</span>
                    <Badge variant="outline" className="text-[10px]">{getRepDisplayName(v.rep_name)}</Badge>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">{v.visit_type || v.entry_type}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">All visits today have follow-ups scheduled</p>
          )}
        </CardContent>
      </Card>

      {/* Section C: Rep Activity Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Activity className="w-4 h-4 text-blue-500" />
            Rep Activity Summary (Today)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {repActivity.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Rep</TableHead>
                  <TableHead className="text-xs text-center">Visits Logged</TableHead>
                  <TableHead className="text-xs text-center">Total Entries</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {repActivity.map(r => (
                  <TableRow key={r.rep}>
                    <TableCell className="text-xs font-medium">{getRepDisplayName(r.rep)}</TableCell>
                    <TableCell className="text-xs text-center">{r.visits}</TableCell>
                    <TableCell className="text-xs text-center">
                      <Badge variant="secondary">{r.entries}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No activity data available</p>
          )}
        </CardContent>
      </Card>

      {/* Section D: Overdue Appointments */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Clock className="w-4 h-4 text-red-500" />
            Overdue Appointments
            <Badge variant={overdueAppts.length > 0 ? "destructive" : "secondary"} className="ml-auto text-[10px]">{overdueAppts.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {overdueAppts.length > 0 ? (
            <div className="space-y-2">
              {overdueAppts.slice(0, 10).map((c, i) => {
                const daysOverdue = c.steps?.[0]?.days_overdue || c.days_overdue || "?";
                return (
                  <div key={i} className="flex items-center justify-between p-2 bg-red-50 rounded-lg border border-red-100 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.patient_name || c.doctor_name || "Case"}</span>
                      <Badge variant="outline" className="text-[10px]">{getRepDisplayName(c.assigned_rep_name)}</Badge>
                    </div>
                    <Badge variant="destructive" className="text-[10px]">{daysOverdue} days overdue</Badge>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 font-medium">No overdue appointments</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


const RepReportsPage = () => {
  // User state - fetch from API instead of relying on prop
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  
  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch (err) {
        console.error("Failed to fetch user:", err);
      } finally {
        setUserLoading(false);
      }
    };
    fetchUser();
  }, []);
  
  const isManager = user?.role === "manager" || user?.role === "admin";
  
  // Filters
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split("T")[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split("T")[0]);
  const [selectedArea, setSelectedArea] = useState("all");
  const [selectedSubregion, setSelectedSubregion] = useState("");
  const [selectedSuburb, setSelectedSuburb] = useState("");
  const [selectedRep, setSelectedRep] = useState("all");
  const [repsList, setRepsList] = useState([]);
  
  // EOD specific
  const [eodDate, setEodDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [eodRepId, setEodRepId] = useState("all");
  
  // Data
  const [summaryData, setSummaryData] = useState(null);
  const [eodData, setEodData] = useState(null);
  const [marketingData, setMarketingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eodLoading, setEodLoading] = useState(false);
  const [marketingLoading, setMarketingLoading] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState("overview");

  // Fetch reps list (for manager dropdown) - only after user loads
  useEffect(() => {
    if (userLoading) return; // Wait for user to load
    
    const fetchRepsList = async () => {
      try {
        const res = await axios.get(`${API}/rep-reports/reps-list`, { withCredentials: true });
        setRepsList(res.data.reps || []);
      } catch (err) {
        console.error("Failed to fetch reps list:", err);
      }
    };
    fetchRepsList();
  }, [userLoading]);

  // Fetch marketing impact data
  const fetchMarketingImpact = useCallback(async () => {
    if (userLoading) return;
    
    setMarketingLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedArea && selectedArea !== "all") params.append("area", selectedArea);
      if (isManager && selectedRep && selectedRep !== "all" && selectedRep !== "me") {
        params.append("rep_name", selectedRep);
      }
      
      const res = await axios.get(`${API}/rep-reports/marketing-impact?${params.toString()}`, { withCredentials: true });
      setMarketingData(res.data);
    } catch (err) {
      console.error("Failed to fetch marketing impact:", err);
    } finally {
      setMarketingLoading(false);
    }
  }, [dateFrom, dateTo, selectedArea, selectedRep, isManager, userLoading]);

  // Fetch summary data
  const fetchSummary = useCallback(async () => {
    if (userLoading) return; // Wait for user to load
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedArea && selectedArea !== "all") params.append("area", selectedArea);
      if (isManager && selectedRep && selectedRep !== "all" && selectedRep !== "me") {
        params.append("rep_name", selectedRep);
      }
      
      const res = await axios.get(`${API}/rep-reports/summary?${params.toString()}`, { withCredentials: true });
      setSummaryData(res.data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
      const status = err.response?.status;
      toast.error(`Failed to load report data${status ? ` (${status})` : ''}`);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedArea, selectedRep, isManager, userLoading]);

  // Fetch EOD data
  const fetchEOD = useCallback(async () => {
    if (userLoading) return; // Wait for user to load
    
    setEodLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("date", eodDate);
      if (isManager && eodRepId && eodRepId !== "all" && eodRepId !== "me") {
        params.append("rep_name", eodRepId);
      }
      
      const res = await axios.get(`${API}/rep-reports/eod?${params.toString()}`, { withCredentials: true });
      setEodData(res.data);
    } catch (err) {
      console.error("Failed to fetch EOD:", err);
      const status = err.response?.status;
      toast.error(`Failed to load EOD report${status ? ` (${status})` : ''}`);
    } finally {
      setEodLoading(false);
    }
  }, [eodDate, eodRepId, isManager, userLoading]);

  // Initial fetch - wait for user first
  useEffect(() => {
    if (!userLoading) {
      fetchSummary();
      fetchMarketingImpact();
    }
  }, [fetchSummary, fetchMarketingImpact, userLoading]);

  useEffect(() => {
    if (activeTab === "eod") {
      fetchEOD();
    }
  }, [activeTab, fetchEOD]);

  // Quick date presets
  const applyDatePreset = (preset) => {
    const today = new Date();
    let from, to;
    
    switch (preset) {
      case "today":
        from = to = today.toISOString().split("T")[0];
        break;
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        from = to = yesterday.toISOString().split("T")[0];
        break;
      case "thisWeek":
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        from = weekStart.toISOString().split("T")[0];
        to = today.toISOString().split("T")[0];
        break;
      case "lastMonth":
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        from = lastMonthStart.toISOString().split("T")[0];
        to = lastMonthEnd.toISOString().split("T")[0];
        break;
      default:
        return;
    }
    
    setDateFrom(from);
    setDateTo(to);
  };

  // EOD date presets
  const applyEodPreset = (preset) => {
    const today = new Date();
    let date;
    
    switch (preset) {
      case "today":
        date = today.toISOString().split("T")[0];
        break;
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        date = yesterday.toISOString().split("T")[0];
        break;
      default:
        return;
    }
    
    setEodDate(date);
  };

  // Helper function to download blob as file
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export functions - use axios with blob response for authenticated downloads
  const exportOverview = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedArea && selectedArea !== "all") params.append("area", selectedArea);
      if (isManager && selectedRep && selectedRep !== "all") params.append("rep_name", selectedRep);
      
      toast.info("Preparing export...");
      const response = await axios.get(`${API}/rep-reports/overview/export?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const filename = `rep_reports_overview_${new Date().toISOString().split("T")[0]}.csv`;
      downloadBlob(response.data, filename);
      toast.success("Overview report exported successfully");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error(`Export failed: ${err.response?.status === 401 ? 'Not authenticated' : 'Server error'}`);
    }
  };

  const exportEODReport = async () => {
    try {
      const params = new URLSearchParams();
      params.append("date", eodDate);
      if (isManager && eodRepId && eodRepId !== "all") params.append("rep_name", eodRepId);
      
      toast.info("Preparing export...");
      const response = await axios.get(`${API}/rep-reports/eod/export?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const filename = `rep_reports_eod_${eodDate}.csv`;
      downloadBlob(response.data, filename);
      toast.success("EOD report exported successfully");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error(`Export failed: ${err.response?.status === 401 ? 'Not authenticated' : 'Server error'}`);
    }
  };

  const exportManagementBreakdown = async () => {
    try {
      const params = new URLSearchParams();
      params.append("date", eodDate);
      
      toast.info("Preparing export...");
      const response = await axios.get(`${API}/rep-reports/breakdown/export?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const filename = `rep_breakdown_${eodDate}.csv`;
      downloadBlob(response.data, filename);
      toast.success("Management breakdown exported successfully");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error(`Export failed: ${err.response?.status === 401 ? 'Not authenticated' : 'Server error'}`);
    }
  };

  const exportMarketingImpact = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedArea && selectedArea !== "all") params.append("area", selectedArea);
      if (isManager && selectedRep && selectedRep !== "all") params.append("rep_name", selectedRep);
      
      toast.info("Preparing export...");
      const response = await axios.get(`${API}/rep-reports/marketing-impact/export?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const filename = `marketing_impact_${new Date().toISOString().split("T")[0]}.csv`;
      downloadBlob(response.data, filename);
      toast.success("Marketing impact report exported successfully");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error(`Export failed: ${err.response?.status === 401 ? 'Not authenticated' : 'Server error'}`);
    }
  };

  // Legacy CSV export for small tables (kept for backward compatibility)
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error("No data to export");
      return;
    }
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map(row => headers.map(h => `"${row[h] || ""}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported successfully");
  };

  // Prepare chart data
  const visitsPerDayData = summaryData?.visit_metrics?.visits_per_day 
    ? Object.entries(summaryData.visit_metrics.visits_per_day)
        .map(([date, count]) => ({ date, visits: count }))
        .sort((a, b) => a.date.localeCompare(b.date))
    : [];

  const visitsByTypeData = summaryData?.visit_metrics?.visits_by_type
    ? Object.entries(summaryData.visit_metrics.visits_by_type)
        .map(([type, count]) => ({ type, count }))
    : [];

  const visitsByAreaData = summaryData?.visit_metrics?.visits_by_area
    ? Object.entries(summaryData.visit_metrics.visits_by_area)
        .map(([area, count]) => ({ area, count }))
    : [];

  const appointmentStatusData = summaryData?.appointment_metrics
    ? [
        { status: "Completed", count: summaryData.appointment_metrics.completed, color: "#22c55e" },
        { status: "Planned", count: summaryData.appointment_metrics.planned, color: "#3b82f6" },
        { status: "No Show", count: summaryData.appointment_metrics.no_show, color: "#ef4444" },
        { status: "Rescheduled", count: summaryData.appointment_metrics.rescheduled, color: "#f59e0b" },
      ]
    : [];

  // Show loading while user is being fetched
  if (userLoading) {
    return (
      <div className="p-4 lg:p-6 space-y-6" data-testid="rep-reports-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Rep Reports</h1>
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <div className="text-muted-foreground">Loading reports...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="rep-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Rep Reports</h1>
          <p className="text-sm text-muted-foreground">
            {isManager ? "View performance analytics for all reps" : "View your performance analytics"}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 lg:w-[500px]">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="eod" data-testid="tab-eod">
            <Clock className="w-4 h-4 mr-2" />
            End of Day
          </TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">
            <Eye className="w-4 h-4 mr-2" />
            Daily Insights
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Filters */}
          <Card data-testid="filters-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quick Date Presets */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => applyDatePreset("today")}
                  data-testid="preset-today"
                >
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => applyDatePreset("yesterday")}
                  data-testid="preset-yesterday"
                >
                  Yesterday
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => applyDatePreset("thisWeek")}
                  data-testid="preset-this-week"
                >
                  This Week
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => applyDatePreset("lastMonth")}
                  data-testid="preset-last-month"
                >
                  Last Month
                </Button>
              </div>
              
              {/* Filter Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    data-testid="filter-date-from"
                  />
                </div>
                <div className="space-y-2">
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    data-testid="filter-date-to"
                  />
                </div>
                <div className="space-y-2">
                  <LocationSelector
                    area={selectedArea === "all" ? "" : selectedArea}
                    subregion={selectedSubregion}
                    suburb={selectedSuburb}
                    onAreaChange={(v) => { setSelectedArea(v || "all"); setSelectedSubregion(""); setSelectedSuburb(""); }}
                    onSubregionChange={(v) => { setSelectedSubregion(v); setSelectedSuburb(""); }}
                    onSuburbChange={(v) => setSelectedSuburb(v)}
                    compact
                  />
                </div>
                {isManager && (
                  <div className="space-y-2">
                    <Label>Rep</Label>
                    <Select value={selectedRep} onValueChange={setSelectedRep}>
                      <SelectTrigger data-testid="filter-rep">
                        <SelectValue placeholder="All Reps" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Reps</SelectItem>
                        {repsList.map((rep) => (
                          <SelectItem key={rep.rep_name} value={rep.rep_name}>
                            {rep.rep_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button onClick={() => { fetchSummary(); fetchMarketingImpact(); }} disabled={loading} data-testid="apply-filters">
                  {loading ? "Loading..." : "Apply Filters"}
                </Button>
                <Button variant="outline" onClick={exportOverview} data-testid="export-overview">
                  <FileDown className="w-4 h-4 mr-2" />
                  Export All
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* KPI Cards */}
          {summaryData && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card data-testid="kpi-total-visits">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Activity className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Visits</p>
                      <p className="text-2xl font-bold">{summaryData.visit_metrics?.total_visits || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card data-testid="kpi-unique-doctors">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        New Doctors
                        <span className="cursor-help text-xs" title="Doctors visited for the first time during the selected period">ⓘ</span>
                      </p>
                      <p className="text-2xl font-bold">{summaryData.visit_metrics?.unique_doctors || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card data-testid="kpi-appointments">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <CalendarDays className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Appointments</p>
                      <p className="text-2xl font-bold">{summaryData.appointment_metrics?.total || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card data-testid="kpi-referrals">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Target className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Referrals</p>
                      <p className="text-2xl font-bold">{summaryData.referral_metrics?.total_referrals || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Appointment Status Cards */}
          {summaryData && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-muted-foreground">Completed</span>
                  </div>
                  <p className="text-xl font-bold text-green-600">{summaryData.appointment_metrics?.completed || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-muted-foreground">No Show</span>
                  </div>
                  <p className="text-xl font-bold text-red-600">{summaryData.appointment_metrics?.no_show || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-muted-foreground">Rescheduled</span>
                  </div>
                  <p className="text-xl font-bold text-amber-600">{summaryData.appointment_metrics?.rescheduled || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-muted-foreground">Planned</span>
                  </div>
                  <p className="text-xl font-bold text-blue-600">{summaryData.appointment_metrics?.planned || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <span className="text-sm text-muted-foreground">Overdue</span>
                  </div>
                  <p className="text-xl font-bold text-orange-600">{summaryData.appointment_metrics?.overdue || 0}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Charts Row */}
          {summaryData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Visits Per Day */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Visits Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  {visitsPerDayData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={visitsPerDayData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="visits" stroke="#3b82f6" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No visit data for selected period</p>
                  )}
                </CardContent>
              </Card>

              {/* Visits by Type */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Visits by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  {visitsByTypeData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={visitsByTypeData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No visit data for selected period</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Visits by Area & Appointment Status */}
          {summaryData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Visits by Area */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Visits by Area</CardTitle>
                </CardHeader>
                <CardContent>
                  {visitsByAreaData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={visitsByAreaData}
                          dataKey="count"
                          nameKey="area"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ area, percent }) => `${area}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {visitsByAreaData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No area data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Appointment Status Pie */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Appointment Status Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  {appointmentStatusData.some(d => d.count > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={appointmentStatusData}
                          dataKey="count"
                          nameKey="status"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ status, count, percent }) => count > 0 ? `${status}: ${(percent * 100).toFixed(0)}%` : ""}
                        >
                          {appointmentStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No appointment data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Top Doctors Table */}
          {summaryData?.visit_metrics?.top_doctors?.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Top Visited Doctors</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(summaryData.visit_metrics.top_doctors, "top_doctors")}
                  data-testid="export-top-doctors"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Doctor</TableHead>
                      <TableHead>Practice</TableHead>
                      <TableHead>Area</TableHead>
                      <TableHead className="text-right">Visits</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summaryData.visit_metrics.top_doctors.map((doc, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{doc.doctor_name}</TableCell>
                        <TableCell>{doc.practice_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{doc.area}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold">{doc.visits}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Marketing Materials Summary */}
          {summaryData?.visit_metrics?.materials_used && Object.keys(summaryData.visit_metrics.materials_used).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Marketing Materials Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(summaryData.visit_metrics.materials_used).map(([material, count]) => (
                    <Badge key={material} variant="secondary" className="text-sm">
                      {material}: {count}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Referral Performance */}
          {summaryData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Referral Performance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{summaryData.referral_metrics?.total_referrals || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Referrals</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{summaryData.referral_metrics?.commission_eligible || 0}</p>
                    <p className="text-sm text-muted-foreground">Commission Eligible</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{summaryData.referral_metrics?.ss_facilitation_completed || 0}</p>
                    <p className="text-sm text-muted-foreground">SS Facilitation</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{summaryData.referral_metrics?.conversion_rate || 0}%</p>
                    <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  </div>
                </div>

                {/* Latest Referrals */}
                {summaryData.referral_metrics?.latest_referrals?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium mb-2">Latest Referrals</h4>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>TIN</TableHead>
                            <TableHead>Patient</TableHead>
                            <TableHead>Doctor</TableHead>
                            <TableHead>Stage</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {summaryData.referral_metrics.latest_referrals.map((ref, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-mono text-xs">{ref.tin_number}</TableCell>
                              <TableCell>{ref.patient_name}</TableCell>
                              <TableCell>{ref.doctor}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{ref.current_stage}</Badge>
                              </TableCell>
                              <TableCell>{ref.referral_date}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Upcoming Appointments */}
          {summaryData?.appointment_metrics?.upcoming?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Upcoming Appointments (Next 14 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Doctor</TableHead>
                      <TableHead>Practice</TableHead>
                      <TableHead>Area</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summaryData.appointment_metrics.upcoming.map((appt, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{appt.appointment_date}</TableCell>
                        <TableCell className="font-medium">{appt.doctor_name}</TableCell>
                        <TableCell>{appt.practice_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{appt.area}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800">{appt.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Marketing Impact Section */}
          <Card data-testid="marketing-impact">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Marketing Impact
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={exportMarketingImpact}
                data-testid="export-marketing-impact"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {marketingLoading ? (
                <p className="text-center text-muted-foreground py-4">Loading marketing data...</p>
              ) : marketingData ? (
                <>
                  {/* Summary Totals */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <p className="text-2xl font-bold">{marketingData.totals?.visits || 0}</p>
                      <p className="text-sm text-muted-foreground">Total Visits</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{marketingData.totals?.referrals || 0}</p>
                      <p className="text-sm text-muted-foreground">Attributed Referrals</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{marketingData.totals?.conversion_rate || 0}%</p>
                      <p className="text-sm text-muted-foreground">Conversion Rate</p>
                    </div>
                  </div>

                  {/* By Material Chart */}
                  {marketingData.by_material && marketingData.by_material.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">By Marketing Material</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={marketingData.by_material}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="material" tick={{ fontSize: 10 }} />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="referrals" fill="#22c55e" name="Referrals" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Detailed Table */}
                  {marketingData.detailed && marketingData.detailed.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Detailed Breakdown</h4>
                      <div className="overflow-x-auto max-h-64">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Area</TableHead>
                              <TableHead>Visit Type</TableHead>
                              <TableHead>Material</TableHead>
                              <TableHead className="text-right">Visits</TableHead>
                              <TableHead className="text-right">Referrals</TableHead>
                              <TableHead className="text-right">Conv. %</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {marketingData.detailed.slice(0, 15).map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{row.area}</TableCell>
                                <TableCell>{row.visit_type}</TableCell>
                                <TableCell>{row.marketing_material}</TableCell>
                                <TableCell className="text-right">{row.visits}</TableCell>
                                <TableCell className="text-right font-medium text-green-600">{row.referrals}</TableCell>
                                <TableCell className="text-right">{row.conversion_rate}%</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center text-muted-foreground py-4">No marketing data available for the selected filters.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* End of Day Tab */}
        <TabsContent value="eod" className="space-y-6">
          {/* EOD Filters */}
          <Card data-testid="eod-filters-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4" />
                End of Day Report
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quick Date Presets */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={eodDate === new Date().toISOString().split("T")[0] ? "default" : "outline"}
                  size="sm"
                  onClick={() => applyEodPreset("today")}
                  data-testid="eod-preset-today"
                >
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => applyEodPreset("yesterday")}
                  data-testid="eod-preset-yesterday"
                >
                  Yesterday
                </Button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={eodDate}
                    onChange={(e) => setEodDate(e.target.value)}
                    data-testid="eod-date-picker"
                  />
                </div>
                {isManager && (
                  <div className="space-y-2">
                    <Label>Rep</Label>
                    <Select value={eodRepId} onValueChange={setEodRepId}>
                      <SelectTrigger data-testid="eod-rep-selector">
                        <SelectValue placeholder="All Reps" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Reps Combined</SelectItem>
                        {repsList.map((rep) => (
                          <SelectItem key={rep.rep_name} value={rep.rep_name}>
                            {rep.rep_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button onClick={fetchEOD} disabled={eodLoading} data-testid="load-eod">
                  {eodLoading ? "Loading..." : "Load EOD Report"}
                </Button>
                {eodData && (
                  <Button variant="outline" onClick={exportEODReport} data-testid="export-eod">
                    <FileDown className="w-4 h-4 mr-2" />
                    Export EOD
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* EOD Results */}
          {eodData && (
            <>
              {/* EOD Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card data-testid="eod-visits">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Activity className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Visits Today</p>
                        <p className="text-2xl font-bold">{eodData.visits?.total || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card data-testid="eod-completed">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Appts Completed</p>
                        <p className="text-2xl font-bold">{eodData.appointments?.completed || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card data-testid="eod-referrals">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Target className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Referrals Created</p>
                        <p className="text-2xl font-bold">{eodData.referrals?.created_today || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card data-testid="eod-overdue">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Overdue Appts</p>
                        <p className="text-2xl font-bold">{eodData.appointments?.overdue_count || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* EOD Details Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Visits Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Visits Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {eodData.visits?.by_type && Object.keys(eodData.visits.by_type).length > 0 ? (
                      <>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">By Type</p>
                          <div className="space-y-1">
                            {Object.entries(eodData.visits.by_type).map(([type, count]) => (
                              <div key={type} className="flex justify-between text-sm">
                                <span>{type}</span>
                                <span className="font-medium">{count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {eodData.visits?.by_area && Object.keys(eodData.visits.by_area).length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-2">By Area</p>
                            <div className="space-y-1">
                              {Object.entries(eodData.visits.by_area).map(([area, count]) => (
                                <div key={area} className="flex justify-between text-sm">
                                  <span>{area}</span>
                                  <span className="font-medium">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">No visits logged</p>
                    )}
                  </CardContent>
                </Card>

                {/* Appointments Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Appointments</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-500" />
                        <span className="text-sm">Planned</span>
                      </div>
                      <span className="font-medium">{eodData.appointments?.planned || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm">Completed</span>
                      </div>
                      <span className="font-medium text-green-600">{eodData.appointments?.completed || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span className="text-sm">No Show</span>
                      </div>
                      <span className="font-medium text-red-600">{eodData.appointments?.no_show || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-amber-500" />
                        <span className="text-sm">Rescheduled</span>
                      </div>
                      <span className="font-medium text-amber-600">{eodData.appointments?.rescheduled || 0}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        <span className="text-sm">Overdue</span>
                      </div>
                      <span className="font-medium text-orange-600">{eodData.appointments?.overdue_count || 0}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Referrals Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Referrals</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Created Today</span>
                      <span className="font-medium">{eodData.referrals?.created_today || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">SS Facilitation Today</span>
                      <span className="font-medium text-green-600">{eodData.referrals?.ss_facilitation_today || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">In SS Facilitation Stage</span>
                      <span className="font-medium text-blue-600">{eodData.referrals?.in_ss_facilitation_stage || 0}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Doctors Today */}
              {eodData.visits?.top_doctors?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Top Doctors Visited Today</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {eodData.visits.top_doctors.map((doc, idx) => (
                        <Badge key={idx} variant="secondary">
                          {doc.doctor_name}: {doc.visits} visit{doc.visits > 1 ? "s" : ""}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Management View - Rep Breakdown */}
              {isManager && eodData.rep_breakdown && Object.keys(eodData.rep_breakdown).length > 0 && (
                <Card data-testid="management-eod-breakdown">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-base">Management Summary - Rep Breakdown</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportManagementBreakdown}
                      data-testid="export-management-eod"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Rep</TableHead>
                            <TableHead className="text-center">Visits</TableHead>
                            <TableHead className="text-center">
                              <CheckCircle2 className="w-4 h-4 inline text-green-500" />
                            </TableHead>
                            <TableHead className="text-center">
                              <XCircle className="w-4 h-4 inline text-red-500" />
                            </TableHead>
                            <TableHead className="text-center">
                              <AlertTriangle className="w-4 h-4 inline text-orange-500" />
                            </TableHead>
                            <TableHead className="text-center">Referrals</TableHead>
                            <TableHead className="text-center">SS Fac.</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(eodData.rep_breakdown).map(([repName, data]) => (
                            <TableRow key={repName}>
                              <TableCell className="font-medium">{repName}</TableCell>
                              <TableCell className="text-center">{data.visits}</TableCell>
                              <TableCell className="text-center text-green-600">{data.appointments_completed}</TableCell>
                              <TableCell className="text-center text-red-600">{data.appointments_no_show}</TableCell>
                              <TableCell className="text-center text-orange-600">{data.overdue}</TableCell>
                              <TableCell className="text-center">{data.referrals_created}</TableCell>
                              <TableCell className="text-center text-blue-600">{data.ss_facilitation_today}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Daily Insights Tab */}
        <TabsContent value="insights" className="space-y-6">
          <DailyInsights user={user} isManager={isManager} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RepReportsPage;
